/**
 * Keeps the header cart count in step with a third-party cart drawer.
 *
 * ## The bug this fixes
 *
 * `assets/cart-icon.js` updates the bubble from exactly one signal: the
 * `shopify:cart:lines-update` event on `document`. Adding through the theme's
 * own product form dispatches it, so the count goes up correctly. Removing
 * happens entirely inside UpCart's drawer, which talks straight to Shopify's
 * Cart AJAX API and never dispatches the theme's event — so the bubble keeps
 * showing the old number over an empty cart.
 *
 * It can outlive a page load, too. `cart-icon.js` writes each count into
 * `sessionStorage['cart-count']` and, on connect, re-applies that value over the
 * server-rendered one when it is less than ten seconds old. So a stale 1 can
 * overwrite a correct 0 on the next page.
 *
 * ## Why it hooks the network rather than UpCart
 *
 * UpCart publishes no event API. Rather than reverse-engineer their internals,
 * this watches for the thing every cart app must do regardless of how it is
 * built: call Shopify's Cart AJAX API. That makes it app-agnostic — it would
 * work the same if UpCart were swapped for another drawer.
 *
 * After a mutation it re-reads `/cart.js` for the authoritative count rather
 * than trying to infer one from the response, then dispatches the event the
 * theme already knows how to handle.
 *
 * ## Two things it is careful about
 *
 * `action` is deliberately not `'add'`. `cart-drawer.js` auto-opens the theme
 * drawer on `action === 'add'`, which would put a second drawer over UpCart's.
 *
 * It dispatches only when the count actually differs from what the bubble shows,
 * so a normal theme add — already handled — does not fire a second time and
 * replay the bubble animation.
 */
(() => {
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  /** Guards against our own /cart.js read being treated as a mutation. */
  let reading = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let pending;

  const isMutation = (url) => {
    if (typeof url !== 'string') return false;
    try {
      // Relative and locale-prefixed paths (/en-ca/cart/change.js) both resolve.
      return CART_MUTATION.test(new URL(url, window.location.origin).pathname + '?');
    } catch {
      return false;
    }
  };

  /** The count the header is currently showing, or null if it cannot be read. */
  const renderedCount = () => {
    const icon = document.querySelector('cart-icon');
    if (!icon) return null;

    // cart-icon renders '' for 100+, which parses to NaN — treat as unknown.
    const count = /** @type {any} */ (icon).currentCartCount;
    return Number.isFinite(count) ? count : null;
  };

  const sync = async () => {
    reading = true;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return;

      const cart = await response.json();
      const itemCount = cart?.item_count ?? 0;

      const shown = renderedCount();
      if (shown === itemCount) return;

      const event = new CustomEvent('shopify:cart:lines-update', { bubbles: true });

      // cart-icon.js reads cart.totalQuantity from the promise; cart-drawer.js
      // reads event.action. Both shapes are satisfied here.
      Object.assign(event, {
        action: 'update',
        promise: Promise.resolve({ cart: { totalQuantity: itemCount }, detail: { itemCount } }),
      });

      document.dispatchEvent(event);
    } catch (error) {
      console.warn('[emberk-cart-count-sync]', error);
    } finally {
      reading = false;
    }
  };

  /** Coalesces the several requests a drawer can fire for one interaction. */
  const scheduleSync = () => {
    clearTimeout(pending);
    pending = setTimeout(sync, 100);
  };

  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const result = originalFetch.apply(this, args);
    if (reading) return result;

    const url = args[0] instanceof Request ? args[0].url : args[0];
    if (isMutation(url)) result.then((response) => response.ok && scheduleSync()).catch(() => {});

    return result;
  };

  // Some apps still use XMLHttpRequest, so cover both rather than assume.
  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (isMutation(url)) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) scheduleSync();
      });
    }
    return originalOpen.call(this, method, url, ...rest);
  };
})();
